<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import {
  CARDS,
  FIELDS,
  SLOT_STEPS,
  DIGEST_WEEKDAYS,
  cardValues,
  validateCard,
  cardPayload,
} from '../settingsCards';
import { CARD_CLASS, FIELD_CLASS, SELECT_CLASS } from '../constants';

// One card of the singabot settings resource. It saves only its own keys
// (partial PUT), so two cards on one page never overwrite each other.
export default {
  name: 'SettingsCard',
  props: {
    cardId: {
      type: String,
      required: true,
      validator: value => Object.keys(CARDS).includes(value),
    },
  },
  data() {
    return {
      cardClass: CARD_CLASS,
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      slotSteps: SLOT_STEPS,
      fields: FIELDS,
      weekdays: DIGEST_WEEKDAYS,
      values: cardValues(this.cardId, null),
      // The stored values the form was last filled from.
      storedSnapshot: '',
      errors: {},
      isSaving: false,
    };
  },
  computed: {
    ...mapGetters('coLivingConfig', { settings: 'getSettings' }),
    card() {
      return CARDS[this.cardId];
    },
    keys() {
      return this.card.keys;
    },
    i18nRoot() {
      return `CO_LIVING.SETTINGS.CARDS.${this.cardId.toUpperCase()}`;
    },
  },
  watch: {
    // The store settings arrive after the page mounts, and again after any
    // save, including a save from another card on the same page. Only reload
    // the form when this card's own stored values changed, so a sibling's save
    // does not wipe what was typed here.
    settings: {
      immediate: true,
      handler(settings) {
        if (!settings) return;
        const stored = cardValues(this.cardId, settings);
        const snapshot = JSON.stringify(stored);
        if (snapshot !== this.storedSnapshot) {
          this.storedSnapshot = snapshot;
          this.values = stored;
        }
      },
    },
  },
  methods: {
    ...mapActions('coLivingConfig', ['saveSettings']),
    fieldType(key) {
      return FIELDS[key].type;
    },
    fieldKey(key) {
      return key.toUpperCase();
    },
    errorText(key) {
      const code = this.errors[key];
      if (!code) return '';
      const { min, max } = FIELDS[key];
      return this.$t(`CO_LIVING.SETTINGS.ERRORS.${code}`, { min, max });
    },
    async onSave() {
      this.errors = validateCard(this.cardId, this.values);
      if (Object.keys(this.errors).length) return;
      this.isSaving = true;
      try {
        await this.saveSettings(cardPayload(this.cardId, this.values));
        useAlert(this.$t('CO_LIVING.COMMON.SAVED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.SETTINGS.SAVE_FAILED'));
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <section :class="cardClass" :data-testid="`co-living-settings-${cardId}`">
    <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100">
      {{ $t(`${i18nRoot}.TITLE`) }}
    </h2>
    <p class="mt-1 mb-4 text-sm text-slate-600 dark:text-slate-300">
      {{ $t(`${i18nRoot}.DESCRIPTION`) }}
    </p>

    <div v-if="card.showTimezone && settings" class="mb-4">
      <span class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
        {{ $t('CO_LIVING.SETTINGS.TIMEZONE') }}
      </span>
      <p
        class="font-mono text-sm text-slate-900 dark:text-slate-100"
        data-testid="co-living-timezone"
      >
        {{ settings.timezone }}
      </p>
      <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
        {{ $t('CO_LIVING.SETTINGS.TIMEZONE_HINT') }}
      </p>
    </div>

    <form class="grid grid-cols-1 gap-4" @submit.prevent="onSave">
      <div v-for="key in keys" :key="key">
        <label
          :for="`co-living-${key}`"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t(`CO_LIVING.SETTINGS.FIELDS.${fieldKey(key)}.LABEL`) }}
        </label>

        <select
          v-if="fieldType(key) === 'bool'"
          :id="`co-living-${key}`"
          v-model="values[key]"
          :class="selectClass"
          :data-testid="`co-living-field-${key}`"
        >
          <option :value="true">
            {{ $t('CO_LIVING.SETTINGS.OPTIONS.YES') }}
          </option>
          <option :value="false">
            {{ $t('CO_LIVING.SETTINGS.OPTIONS.NO') }}
          </option>
        </select>

        <select
          v-else-if="fieldType(key) === 'slot'"
          :id="`co-living-${key}`"
          v-model="values[key]"
          :class="selectClass"
          :data-testid="`co-living-field-${key}`"
        >
          <option v-for="step in slotSteps" :key="step" :value="step">
            {{ step }}
          </option>
        </select>

        <input
          v-else-if="fieldType(key) === 'prefix'"
          :id="`co-living-${key}`"
          v-model="values[key]"
          type="text"
          maxlength="6"
          :class="[fieldClass, errors[key] ? 'border-red-500' : '']"
          :data-testid="`co-living-field-${key}`"
          @input="values[key] = values[key].toUpperCase()"
        />

        <input
          v-else-if="fieldType(key) === 'time'"
          :id="`co-living-${key}`"
          v-model="values[key]"
          type="time"
          class="max-w-xs"
          :class="[fieldClass, errors[key] ? 'border-red-500' : '']"
          :data-testid="`co-living-field-${key}`"
        />

        <div
          v-else-if="fieldType(key) === 'days'"
          :id="`co-living-${key}`"
          class="flex flex-wrap gap-3"
          :data-testid="`co-living-field-${key}`"
        >
          <label
            v-for="day in weekdays"
            :key="day"
            class="flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200"
          >
            <input
              v-model="values[key]"
              type="checkbox"
              :value="day"
              class="mb-0"
              :data-testid="`co-living-day-${key}-${day}`"
            />
            {{ $t(`CO_LIVING.WEEKDAYS.${day}`).slice(0, 3) }}
          </label>
        </div>

        <input
          v-else
          :id="`co-living-${key}`"
          v-model="values[key]"
          type="number"
          :step="fields[key].type === 'decimal' ? 'any' : 1"
          :min="fields[key].min"
          :max="fields[key].max"
          :class="[fieldClass, errors[key] ? 'border-red-500' : '']"
          :data-testid="`co-living-field-${key}`"
        />

        <p
          v-if="errors[key]"
          class="mt-1 text-xs text-red-500"
          :data-testid="`co-living-error-${key}`"
        >
          {{ errorText(key) }}
        </p>
        <p v-else class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t(`CO_LIVING.SETTINGS.FIELDS.${fieldKey(key)}.HINT`) }}
        </p>
      </div>

      <div>
        <woot-button
          type="submit"
          :is-loading="isSaving"
          :data-testid="`co-living-save-${cardId}`"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </section>
</template>
